import { useLayoutEffect, useRef } from 'react';
import NavLevel2Link from './NavLevel2Link';
import CustomImage from '../../fragments/custom-image/CustomImage';

// Level 2 — the dropdown panel that opens under a Level 1 menu item.
//
// Every panel is mounted for every Level 1 item that has children; only the one
// on the `.hover` item is visible. The open panel's height (and the white nav
// background behind it) is driven off the measured height of `-inner` rather
// than a fixed `--max-nav-height`, so a three-link menu doesn't leave 400px of
// empty white below it. `onHeight` hands the measurement up to
// NavigationDesktop, which publishes it as `--nav-panel-height` on the nav.
export default function NavLevel2Menu({
  children,
  title,
  uri,
  image,
  excerpt,
  id,
  isOpen,
  onHeight,
}) {
  const innerRef = useRef(null);

  const parentLink =
    uri && uri != '/'
      ? { id: '__parent__', uri, label: 'Overview', classes: [] }
      : null;
  const items = parentLink ? [parentLink, ...children] : children;

  // Layout effect so the height lands in the same paint as the `.hover` class —
  // the CSS transition then runs from the closed height rather than snapping.
  useLayoutEffect(() => {
    const inner = innerRef.current;
    if (!isOpen || !inner || !onHeight) return;

    let frame = null;

    // `offsetHeight` is already clamped by the inner's own max-height, so a
    // panel taller than the viewport stops there and scrolls internally.
    const measure = () => {
      frame = null;
      onHeight(inner.offsetHeight);
    };

    measure();

    // Re-measure on viewport resize, font load and image load. ResizeObserver
    // fires per frame while a window is being dragged; coalesce to one
    // measurement per animation frame.
    const resizeObserver = new ResizeObserver(() => {
      if (frame !== null) return;
      frame = requestAnimationFrame(measure);
    });
    resizeObserver.observe(inner);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      resizeObserver.disconnect();
    };
  }, [isOpen, onHeight]);

  return (
    <div className="NavLevel2Menu" id={id}>
      <div className="NavLevel2Menu-inner" ref={innerRef}>
        {/* {title && <p className="NavLevel2Menu-content-title">{title}</p>} */}
        <div className="NavLevel2Menu-nav">
          <div className="NavLevel2Menu-navInner">
            <ul className="NavLevel2Menu-list">
              {items.slice(0, 7).map((child, index) => (
                <NavLevel2Link child={child} index={index} key={child.id} />
              ))}
            </ul>
          </div>
        </div>
        {(image || excerpt) && (
          <div className="NavLevel2Menu-media">
            {excerpt && <p className="NavLevel2Menu-excerpt">{excerpt}</p>}
            {image && (
              <CustomImage
                image={image}
                fill
                fillMedia
                className="NavLevel2Menu-mediaItem"
                sizes="(max-width: 1023px) 100vw, 420px"
                // reveal={true}
                // delay={2}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
